import React, { useMemo } from 'react';
import { 
  FiPackage, FiClock, FiUser, FiTruck, FiCheck, FiX, 
  FiAlertCircle, FiChevronDown, FiMapPin, FiDollarSign,
  FiMessageSquare, FiPhone, FiRefreshCw
} from 'react-icons/fi';
import TicketCard from './TicketCard';

// Status colors for the timeline
const STATUS_COLORS = {
  completed: 'bg-emerald-500',
  in_progress: 'bg-sky-500',
  pending: 'bg-gray-300',
  blocked: 'bg-red-500',
  cancelled: 'bg-gray-400',
};

// Ticket type icons
const TYPE_ICONS = {
  PLACE_ORDER: FiPackage,
  SET_DELIVERY_TIME: FiClock,
  VERIFY_ENTRY: FiUser,
  SET_DRIVER_PAY: FiDollarSign,
  FIND_DRIVER: FiTruck,
  DRIVER_SUPPORT: FiMessageSquare,
  CONTACT_HOST: FiPhone,
  EOD_REVIEW: FiCheck,
  MORNING_CHECK: FiRefreshCw,
  HANDLE_REFUND: FiX,
  DEFAULT: FiAlertCircle,
};

export default function OrderStory({ 
  order, 
  tickets = [], 
  onTicketAction,
  onClose,
  expanded = true 
}) {
  // Group tickets by status for visual summary
  const ticketStats = useMemo(() => {
    const stats = { completed: 0, in_progress: 0, pending: 0, blocked: 0 };
    tickets.forEach(t => {
      const status = t.status || 'pending';
      if (stats[status] !== undefined) stats[status]++;
    });
    return stats;
  }, [tickets]);

  // Sort tickets by creation time for timeline
  const sortedTickets = useMemo(() => {
    return [...tickets].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [tickets]);

  // Calculate overall progress
  const progress = tickets.length > 0 
    ? Math.round((ticketStats.completed / tickets.length) * 100)
    : 0;

  if (!order) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <FiPackage className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Order #{order.uniqueId}</h2>
              <p className="text-gray-300 text-sm flex items-center gap-2">
                <FiMapPin className="w-3 h-3" />
                {order.property?.address?.city || 'Unknown location'}
                <span className="text-gray-500">•</span>
                <FiClock className="w-3 h-3" />
                {order.inStockDate ? new Date(order.inStockDate).toLocaleDateString() : 'No date'}
              </p>
            </div>
          </div>
          
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-300">Story Progress</span>
            <span className="font-bold">{progress}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stats Pills */}
        <div className="flex items-center gap-3 mt-4">
          <StatPill label="Completed" count={ticketStats.completed} color="emerald" />
          <StatPill label="In Progress" count={ticketStats.in_progress} color="sky" />
          <StatPill label="Pending" count={ticketStats.pending} color="gray" />
          {ticketStats.blocked > 0 && (
            <StatPill label="Blocked" count={ticketStats.blocked} color="red" />
          )}
        </div>
      </div>

      {/* Order Details Quick View */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <QuickStat 
            label="Guest" 
            value={order.guestEmail?.split('@')[0] || 'N/A'} 
            icon={FiUser}
          />
          <QuickStat 
            label="Store Spend" 
            value={order.storeSpend ? `$${parseFloat(order.storeSpend).toFixed(2)}` : '—'} 
            icon={FiDollarSign}
            highlight={!order.storeSpend}
          />
          <QuickStat 
            label="Concierge" 
            value={order.driverID ? 'Assigned' : 'Unassigned'} 
            icon={FiTruck}
            highlight={!order.driverID}
          />
          <QuickStat 
            label="Status" 
            value={order.status || 'Unknown'} 
            icon={FiAlertCircle}
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="px-6 py-4">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiClock className="w-4 h-4 text-gray-400" />
          Ticket Timeline
        </h3>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />

          {/* Tickets */}
          <div className="space-y-4">
            {sortedTickets.map((ticket, idx) => {
              const TypeIcon = TYPE_ICONS[ticket.type] || TYPE_ICONS.DEFAULT;
              const status = ticket.status || 'pending';
              const statusColor = STATUS_COLORS[status] || STATUS_COLORS.pending;

              return (
                <div key={ticket.id || idx} className="relative pl-12">
                  {/* Timeline dot */}
                  <div className={`
                    absolute left-3 top-4 w-5 h-5 rounded-full border-2 border-white dark:border-gray-800
                    flex items-center justify-center ${statusColor}
                    ${status === 'in_progress' ? 'animate-pulse' : ''}
                  `}>
                    {status === 'completed' && <FiCheck className="w-3 h-3 text-white" />}
                  </div>

                  {/* Ticket Card */}
                  <TicketCard
                    ticket={ticket}
                    isUrgent={ticket.isUrgent}
                    isOverdue={ticket.isOverdue}
                    isWorking={status === 'in_progress'}
                    onStart={() => onTicketAction?.('start', ticket)}
                    onComplete={() => onTicketAction?.('complete', ticket)}
                    onPause={() => onTicketAction?.('pause', ticket)}
                    compact
                  />

                  {/* Completion info */}
                  {status === 'completed' && ticket.completedAt && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <FiCheck className="w-3 h-3 text-emerald-500" />
                      Completed by {ticket.completedByName || 'Unknown'} 
                      <span className="text-gray-400">•</span>
                      {new Date(ticket.completedAt).toLocaleString()}
                      {ticket.durationMinutes && (
                        <>
                          <span className="text-gray-400">•</span>
                          Took {ticket.durationMinutes}m
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty state */}
          {sortedTickets.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FiPackage className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No tickets generated for this order yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Activity Log (collapsed by default) */}
      <details className="border-t border-gray-200 dark:border-gray-700">
        <summary className="px-6 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between">
          <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">Activity Log</span>
          <FiChevronDown className="w-4 h-4 text-gray-400" />
        </summary>
        <div className="px-6 py-4 max-h-64 overflow-y-auto bg-gray-50 dark:bg-gray-900/50">
          {/* Activity entries would go here */}
          <div className="space-y-2 text-xs">
            {sortedTickets.filter(t => t.status === 'completed').map((ticket, idx) => (
              <div key={idx} className="flex items-start gap-3 text-gray-600 dark:text-gray-400">
                <span className="text-gray-400 whitespace-nowrap">
                  {ticket.completedAt ? new Date(ticket.completedAt).toLocaleTimeString() : '—'}
                </span>
                <span>
                  <strong>{ticket.completedByName || 'Unknown'}</strong> completed "{ticket.name || ticket.type}"
                </span>
              </div>
            ))}
          </div>
        </div>
      </details>
    </div>
  );
}

function StatPill({ label, count, color }) {
  const colors = {
    emerald: 'bg-emerald-500/20 text-emerald-300',
    sky: 'bg-sky-500/20 text-sky-300',
    gray: 'bg-white/10 text-gray-300',
    red: 'bg-red-500/20 text-red-300',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[color]}`}>
      {count} {label}
    </span>
  );
}

function QuickStat({ label, value, icon: Icon, highlight = false }) {
  return (
    <div className={`flex items-center gap-2 ${highlight ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'}`}>
      <Icon className="w-4 h-4 text-gray-400" />
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className="font-medium text-sm">{value}</p>
      </div>
    </div>
  );
}

